import { MultiTextInput } from '@delmonicos/shared-design-system-react/src/components/input/multi-text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Text } from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { useFulfillRfidBadgeOrderFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/user-service-lib/src/wrapper/inputs/rfid-badge-orders/fulfill-rfid-badge-order.form-validation';
import { FulfillRfidBadgeOrderModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/user-service-lib/src/wrapper/inputs/rfid-badge-orders/fulfill-rfid-badge-order.model';
import React, { useState } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useEmspBoRfidBadgeOrderContext } from '../../emsp-bo-rfid-badge-order.context';

const EmspBoRfidBadgeOrderFulfillFormStep1RfidBadgeVisualNumbersSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<
    FulfillRfidBadgeOrderModel,
    'rfidBadgeVisualNumbers'
  >;
  fieldState: ControllerFieldState;
}) => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // INPUT PROPERTIES //////////////////////////////////////////////////////////

  const [value, setValue] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // ITEM LIST /////////////////////////////////////////////////////////////////

  const [itemList, setItemList] = useState<string[]>(field.value ?? []);

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleChange = (itemListChanged: string[]) => {
    setItemList(itemListChanged);
    field.onChange(itemListChanged);
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack gap={GapEnum.XS} direction="column">
      <Text>
        {t(
          'delmonicos-shared-sdk.outputs-rfid-badge-order.rfid-badge-visual-numbers',
        )}
      </Text>
      <MultiTextInput
        placeholder={t(
          'delmonicos-shared-sdk.outputs-rfid-badge-order.rfid-badge-visual-numbers',
        )}
        inputValue={value}
        items={itemList}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key !== 'Enter') {
            return;
          }

          e.preventDefault();

          const item = value.trim();

          if (item === '') {
            setErrorMessage('');
            return;
          }

          if (itemList.includes(item)) {
            setErrorMessage(
              t('$.emsp-bo-rfid-badge-order-fulfill.step-1.duplicate'),
            );
            return;
          }

          handleChange([...itemList, value.trim()]);

          setValue('');
          setErrorMessage('');
        }}
        onAddItem={(item) => {
          if (item === '') {
            setErrorMessage('');
            return;
          }

          if (itemList.includes(item)) {
            setErrorMessage(
              t('$.emsp-bo-rfid-badge-order-fulfill.step-1.duplicate'),
            );
            return;
          }

          handleChange([...itemList, item]);

          setValue('');
          setErrorMessage('');
        }}
        onRemoveItem={(index) => {
          handleChange(itemList.filter((_, i) => i !== index));
        }}
        onInputChange={setValue}
        errorMessage={errorMessage || error?.message}
      />
    </Stack>
  );
};

export const EmspBoRfidBadgeOrderFulfillFormStep1Controller =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    const { t } = useTranslation();
    const { control } = useFormContext<FulfillRfidBadgeOrderModel>();
    const { rfidBadgeVisualNumbers } = useFulfillRfidBadgeOrderFormValidation();
    const order = useEmspBoRfidBadgeOrderContext();

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <Stack direction="column" gap={GapEnum.L}>
        <Text>
          {t('$.emsp-bo-rfid-badge-order-fulfill.step-1.description', {
            quantity: order.quantity,
          })}
        </Text>
        <Controller
          name="rfidBadgeVisualNumbers"
          control={control}
          rules={rfidBadgeVisualNumbers}
          render={
            EmspBoRfidBadgeOrderFulfillFormStep1RfidBadgeVisualNumbersSelection
          }
        />
      </Stack>
    );
  };
